import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Table, Pagination, PaginationItem, PaginationLink } from '../../../shim/reactstrap';
import apiClient from '../../../api/ApiClient';

/**
 * Admin dialog listing the structured warnings produced by a persisted
 * spreadsheet calculation. Today every warning is a date-alignment removal
 * record shaped like
 * <code>{ Type: "RemovedRows", Date: "1988-02-06T00:00:00", DataSource: ["SCUDDER HIGH YLD"] }</code>
 * — i.e. one record per removed row, carrying the calendar date and the
 * list of data sources that never contained it.
 *
 * The list is fetched lazily from
 * <c>GET /api/spreadsheets/{id}/warnings</c> when the modal is
 * opened so the per-page rows payload does not have to carry this
 * potentially-long list every time the user paginates.
 *
 * Pagination is local (in-memory over the returned list) because the
 * backend returns the full list in a single response.
 */
export const WarningsModal = ({
  isOpen,
  toggle,
  calculationDbId,
  calculationTitle,
  dataSourceLabel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const fetchWarnings = useCallback(async () => {
    if (!calculationDbId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.getModelSpreadsheetWarnings(calculationDbId);
      // Backend returns `{ CalculationDbId, Warnings: ModelSpreadsheetWarning[] }`.
      // Each entry is a { Type, Date, DataSource } object — see
      // ModelSpreadsheetWarning on the server. The field used to be a
      // free-form string array named RemovedRows before the rename + shape
      // change in migration 061.
      setWarnings(Array.isArray(result?.Warnings) ? result.Warnings : []);
      setCurrentPage(1);
    } catch (exc) {
      console.error('Failed to fetch warnings:', exc);
      setError(exc.message || 'Failed to fetch warnings');
      setWarnings([]);
    } finally {
      setLoading(false);
    }
  }, [calculationDbId]);

  useEffect(() => {
    if (isOpen && calculationDbId) {
      fetchWarnings();
    } else if (!isOpen) {
      // Reset state when the modal closes so the next open shows a clean
      // spinner rather than the previous calculation's data.
      setWarnings([]);
      setError(null);
      setCurrentPage(1);
    }
  }, [isOpen, calculationDbId, fetchWarnings]);

  const formatWarningType = (type) => {
    if (!type) return 'Removed Rows';
    // PascalCase or SCREAMING_SNAKE -> human friendly
    return String(type)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  };

  const formatDate = (date) => {
    if (!date) return null;
    // Backend serialises DateTime as ISO 8601 strings. Calendar-date
    // values (e.g. "1988-02-06T00:00:00") carry their date in UTC; do
    // NOT re-interpret them in local time, otherwise the rendered date
    // shifts by the user's UTC offset. Take the leading YYYY-MM-DD
    // as-is, falling back to the ISO form for fully-qualified
    // timestamps.
    if (typeof date === 'string') {
      const m = date.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const renderMissingSources = (dataSource) => {
    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      return <span className="text-muted">—</span>;
    }
    return (
      <ul className="warnings-source-list mb-0">
        {dataSource.map((source, idx) => (
          <li key={`${source}-${idx}`}>
            <code>{source}</code>
          </li>
        ))}
      </ul>
    );
  };

  const handlePageChange = (page) => {
    const totalPages = Math.max(1, Math.ceil(warnings.length / pageSize));
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    const totalPages = Math.max(1, Math.ceil(warnings.length / pageSize));
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PaginationItem key="first">
          <PaginationLink first onClick={() => handlePageChange(1)} />
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
      </PaginationItem>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
      </PaginationItem>
    );

    if (endPage < totalPages) {
      pages.push(
        <PaginationItem key="last">
          <PaginationLink last onClick={() => handlePageChange(totalPages)} />
        </PaginationItem>
      );
    }

    return <Pagination>{pages}</Pagination>;
  };

  const title = calculationTitle || `Calculation #${calculationDbId}`;
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, warnings.length);
  const pageSlice = warnings.slice(pageStart, pageEnd);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Warnings &mdash; {title}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          {dataSourceLabel && (
            <>
              <strong>Data source:</strong> <code>{dataSourceLabel}</code>{' '}
            </>
          )}
          {calculationDbId != null && (
            <>
              <strong className={dataSourceLabel ? 'ms-3' : ''}>Calculation:</strong>{' '}
              <code>#{calculationDbId}</code>
            </>
          )}
        </div>

        <div className="mb-3">
          <small className="text-muted">
            Each row lists one warning produced during this run. Today every
            warning is a date-alignment removal: a row was dropped because its
            date was unique to a just-read data source. The Date column shows
            the row's calendar date and the &quot;Missing data sources&quot;
            column lists the other sources that never contained it.
          </small>
        </div>

        {error && (
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading warnings...</div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="mb-3">
              <strong>Total warnings:</strong> {warnings.length}
              {warnings.length > 0 && (
                <span className="ms-2 text-muted">
                  (Showing {pageStart + 1} &ndash; {pageEnd})
                </span>
              )}
            </div>

            {warnings.length > 0 ? (
              <>
                <div className="table-responsive warnings-modal-scroll">
                  <Table striped bordered size="sm" className="warnings-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th style={{ width: '140px' }}>Type</th>
                        <th style={{ width: '140px' }}>Date</th>
                        <th>Missing data sources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageSlice.map((warning, idx) => {
                        const typeLabel = formatWarningType(warning?.Type);
                        const dateLabel = formatDate(warning?.Date);
                        return (
                          <tr key={`${pageStart + idx}`}>
                            <td><strong>{pageStart + idx + 1}</strong></td>
                            <td className="warnings-type-cell">{typeLabel}</td>
                            <td className="warnings-date-cell">
                              {dateLabel ? <code>{dateLabel}</code> : <span className="text-muted">—</span>}
                            </td>
                            <td className="warnings-message-cell">
                              {renderMissingSources(warning?.DataSource)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-center mt-3">
                  {renderPagination()}
                </div>
              </>
            ) : (
              <Alert color="success" fade={false}>
                No warnings were produced during this run.
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WarningsModal;
